import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for animating percentage counters when section comes into view
 * @param {number} targetPercentage - Target percentage to animate to (default: 100)
 * @param {number} duration - Animation duration in milliseconds (default: 2000)
 * @param {number} delay - Delay before starting animation in milliseconds (default: 200)
 * @returns {Object} { ref, percentage, isVisible }
 */
export const usePercentageAnimation = (targetPercentage = 100, duration = 2000, delay = 200) => {
    const ref = useRef(null);
    const [percentage, setPercentage] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    setIsVisible(true);
                    
                    // Start animation after delay
                    setTimeout(() => {
                        animatePercentage();
                    }, delay);
                }
            },
            {
                threshold: 0.3, // Trigger when 30% of the element is visible
                rootMargin: '0px 0px -50px 0px' // Start animation slightly before element is fully visible
            }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [isVisible, delay]);

    const animatePercentage = () => {
        const startTime = Date.now();
        const startPercentage = 0;

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function for smooth animation (ease-out)
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentPercentage = Math.round(startPercentage + (targetPercentage - startPercentage) * easeOut);
            
            setPercentage(currentPercentage);

            // Also update the progress bar width directly via JavaScript
            if (ref.current) {
                const progressBar = ref.current.querySelector('.skill-progress');
                if (progressBar) {
                    progressBar.style.width = `${currentPercentage}%`;
                }
            }

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setPercentage(targetPercentage);
                // Ensure final state
                if (ref.current) {
                    const progressBar = ref.current.querySelector('.skill-progress');
                    if (progressBar) {
                        progressBar.style.width = `${targetPercentage}%`;
                    }
                }
            }
        };

        requestAnimationFrame(animate);
    };

    return { ref, percentage, isVisible };
};

export default usePercentageAnimation;
